/**
 * Topic Chat Panel — צ'אט ממוקד נושא
 *
 * The free chat that opens after the reader presses the lesson's CTA. Every
 * message carries the track and the topic slug, so the guide answers within the
 * lesson's scope and the turn is counted against that topic's progress.
 *
 * This is the ONLY part of a track that spends the user's AI allowance.
 */

import { useEffect, useRef } from 'react'
import { AlertCircle, GraduationCap } from 'lucide-react'
import MessageBubble from '../chat/MessageBubble'
import TypingIndicator from '../chat/TypingIndicator'
import ChatInput from '../chat/ChatInput'
import SuggestedQuestions from './SuggestedQuestions'
import { useLearningStore } from '../../store/learningStore'
import { useAuthStore } from '../../store/authStore'
import { TRACKS, type TrackKey } from '../../config/tracks'

interface TopicChatPanelProps {
  /** Defaults to the student track so existing usages keep working. */
  track?: TrackKey
  topicSlug: string
  topicTitle: string
  suggestedQuestions: string[]
}

export default function TopicChatPanel({
  track = 'student',
  topicSlug,
  topicTitle,
  suggestedQuestions,
}: TopicChatPanelProps) {
  const { user } = useAuthStore()
  const {
    chatMessages,
    chatLoading,
    chatError,
    capReached,
    usedSuggestions,
    sendTopicMessage,
  } = useLearningStore()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [chatMessages, chatLoading])

  const handleSend = (text: string) => {
    void sendTopicMessage(track, topicSlug, text)
  }

  return (
    <section
      aria-labelledby="topic-chat-heading"
      className="flex h-[32rem] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white"
    >
      <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
        <span className="rounded-full bg-primary-100 p-2 text-primary-700">
          <GraduationCap size={18} aria-hidden="true" />
        </span>
        <div>
          <h2 id="topic-chat-heading" className="text-sm font-bold text-gray-900">
            שאלות על {topicTitle}
          </h2>
          <p className="text-xs text-gray-600">כלי חינוכי — לא ייעוץ אישי</p>
        </div>
      </div>

      <div
        ref={containerRef}
        className="flex-1 space-y-3 overflow-y-auto bg-gray-50 p-4"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label={`שיחה על ${topicTitle}`}
      >
        {chatMessages.length === 0 && (
          <p className="mt-6 px-4 text-center text-sm text-gray-700">
            קראת את השיעור — עכשיו אפשר לשאול כל מה שלא הובן. אין שאלה טיפשית.
          </p>
        )}
        {chatMessages.map((m, i) => (
          <MessageBubble key={i} message={m} userName={user?.firstName} />
        ))}
        {chatLoading && <TypingIndicator />}
      </div>

      {chatError && (
        <div className="flex items-center gap-2 bg-red-50 p-3 text-sm text-red-800" role="alert">
          <AlertCircle size={16} aria-hidden="true" />
          {chatError}
        </div>
      )}

      {!capReached && (
        <SuggestedQuestions
          questions={suggestedQuestions}
          used={usedSuggestions}
          disabled={chatLoading}
          onPick={handleSend}
        />
      )}

      {capReached ? (
        <p className="border-t border-gray-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {TRACKS[track].capReachedMessage}
        </p>
      ) : (
        <ChatInput
          onSend={handleSend}
          disabled={chatLoading}
          hideQuotaInfo
          placeholder={`שאל/י על ${topicTitle}...`}
        />
      )}
    </section>
  )
}
